import {AxiosResponse} from 'axios';
import {
    EfeResponse,
    AddressServiceRequest,
    ClientContactServiceRequest,
    ConstitutiveActServiceRequest,
    TransactionalProfileServiceRequest,
    WorkflowServiceRequest,
    WorkflowServiceResponse,
    ShareholderServiceRequest,
    LegalRepresentativeServiceRequest,
    RealOwnerServiceRequest,
    IdentificationServiceRequest,
    InformationLegalRepresentativeServiceRequest,
    PersonalDataServiceRequest,
    RealOwnerAdditionalInformation,
    ValidateClientServiceRequest,
    DashboardUsersValues,
    LegalRepresentativesResponse,
    ShareholdersResponse,
    RealOwnersResponse,
    SyncClientDataResponse,
    SignTermsConditionsResponse,
    ExpedientResponse,
    BeneficiariesResponse,
    BeneficiaryServiceRequest,
    BeneficiariesAuthorization,
} from 'commons/models';
import {
    ClientResponse,
    ConsigneeCatalogResponse,
    ConsigneeConfirmation,
    ConsigneeData,
    ConsigneeTerms,
    ConsigneesResponse,
    GeolocationInfo,
    MailAuthResponse,
    OtpValidation,
} from 'commons/types/Types';
import EfeApi from 'services/api/Api';
import {
    LogDetailRequest, LogDetailResponse,
    LogErrorsRequest,
    LogErrorsResponse,
} from '../../commons/models/dtos/logs/Logs';

async function getWorkflow(expedientId: number): Promise<EfeResponse<WorkflowServiceResponse>> {
    try {
        return await EfeApi.Clients.get<WorkflowServiceResponse>(
            `/api/v1/clients/workflow/${expedientId}`
        ).then((res: AxiosResponse<WorkflowServiceResponse>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveAddress(
    request: AddressServiceRequest,
    foreign: boolean
): Promise<EfeResponse<AddressServiceRequest>> {
    let url = '/api/v1/clients/personal/address';
    if (foreign) {
        url = '/api/v1/clients/foreigners/personal/address';
    }
    try {
        return await EfeApi.Clients.put<AddressServiceRequest>(url, request).then(
            (res: AxiosResponse<AddressServiceRequest>) => {
                return Promise.resolve({
                    data: res.data,
                    status: 'Success',
                    success: true,
                    code: res.status,
                    message: 'Ok',
                });
            }
        );
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveWorkflow(
    request: WorkflowServiceRequest
): Promise<EfeResponse<WorkflowServiceResponse>> {
    try {
        return await EfeApi.Clients.post<WorkflowServiceResponse>(
            '/api/v1/clients/workflow',
            request
        ).then((res: AxiosResponse<WorkflowServiceResponse>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveClientContact(
    request: ClientContactServiceRequest
): Promise<EfeResponse<ClientContactServiceRequest>> {
    try {
        return await EfeApi.Clients.post<ClientContactServiceRequest>(
            '/api/v1/clients/enrollment',
            request
        ).then((res: AxiosResponse<ClientContactServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function syncClientData(expedientId: string): Promise<EfeResponse<SyncClientDataResponse>> {
    try {
        return await EfeApi.AmlSync.post(`/api/v1/clients/aml?expedientId=${expedientId}`).then(
            (res: AxiosResponse<SyncClientDataResponse>) => {
                return Promise.resolve({
                    data: res.data,
                    status: 'Success',
                    success: true,
                    code: res.status,
                    message: 'Ok',
                });
            }
        );
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getClientContact(rfc: string): Promise<EfeResponse<ClientContactServiceRequest>> {
    try {
        return await EfeApi.Clients.get<ClientContactServiceRequest>('/api/v1/clients/enrollment', {
            params: {rfc: rfc},
        }).then((res: AxiosResponse<ClientContactServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function updateConstitutiveAct(
    request: ConstitutiveActServiceRequest
): Promise<EfeResponse<ConstitutiveActServiceRequest>> {
    try {
        return await EfeApi.Clients.put<ConstitutiveActServiceRequest>(
            '/api/v1/clients/documents/constitutive/information',
            request
        ).then((res: AxiosResponse<ConstitutiveActServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function updatePersonalData(
    personTypeKey: string,
    request: PersonalDataServiceRequest
): Promise<EfeResponse<PersonalDataServiceRequest>> {
    try {
        return await EfeApi.Clients.put<PersonalDataServiceRequest>(
            `/api/v1/clients/personal/data/${personTypeKey}`,
            request
        ).then((res: AxiosResponse<PersonalDataServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveTransactionProfile(
    request: TransactionalProfileServiceRequest
): Promise<EfeResponse<TransactionalProfileServiceRequest>> {
    try {
        return await EfeApi.Clients.put<TransactionalProfileServiceRequest>(
            '/api/v1/clients/pld',
            request
        ).then((res: AxiosResponse<TransactionalProfileServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveRealOwnerAddress(
    request: AddressServiceRequest
): Promise<EfeResponse<AddressServiceRequest>> {
    try {
        return await EfeApi.Clients.put<AddressServiceRequest>(
            '/api/v1/clients/real/owner/address',
            request
        ).then((res: AxiosResponse<AddressServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveShareholderData(
    request: ShareholderServiceRequest,
    personTypeKey: string | undefined
): Promise<EfeResponse<ShareholderServiceRequest>> {
    try {
        return await EfeApi.Clients.put<ShareholderServiceRequest>(
            `/api/v1/clients/personal/data/shareholder/${personTypeKey ? personTypeKey : ''}`,
            request
        ).then((res: AxiosResponse<ShareholderServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveLegalRepresentative(
    request: LegalRepresentativeServiceRequest
): Promise<EfeResponse<LegalRepresentativeServiceRequest>> {
    try {
        return await EfeApi.Clients.put<LegalRepresentativeServiceRequest>(
            '/api/v1/clients/personal/data/legal/representative',
            request
        ).then((res: AxiosResponse<LegalRepresentativeServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveRealOwner(
    request: RealOwnerServiceRequest
): Promise<EfeResponse<RealOwnerServiceRequest>> {
    try {
        return await EfeApi.Clients.put<RealOwnerServiceRequest>(
            '/api/v1/clients/personal/data/real/owner',
            request
        ).then((res: AxiosResponse<RealOwnerServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveIdentificationData(
    request: IdentificationServiceRequest
): Promise<EfeResponse<IdentificationServiceRequest>> {
    try {
        return await EfeApi.Clients.put<IdentificationServiceRequest>(
            '/api/v1/clients/identifications/data',
            request
        ).then((res: AxiosResponse<IdentificationServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveLegalRepresentativeOfficialInformationData(
    request: InformationLegalRepresentativeServiceRequest
): Promise<EfeResponse<InformationLegalRepresentativeServiceRequest>> {
    try {
        return await EfeApi.Clients.put<InformationLegalRepresentativeServiceRequest>(
            '/api/v1/clients/legals/representatives/identifications/data',
            request
        ).then((res: AxiosResponse<InformationLegalRepresentativeServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getRealOwnerAdditionalInformation(
    kycId: number
): Promise<EfeResponse<RealOwnerAdditionalInformation>> {
    try {
        return await EfeApi.Clients.get<RealOwnerAdditionalInformation>(
            `/api/v1/clients/real/owner/additional/information/${kycId}`
        ).then((res: AxiosResponse<RealOwnerAdditionalInformation>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveRealOwnerAdditionalInformation(
    request: RealOwnerAdditionalInformation
): Promise<EfeResponse<RealOwnerAdditionalInformation>> {
    try {
        return await EfeApi.Clients.put<RealOwnerAdditionalInformation>(
            '/api/v1/clients/real/owner/additional/information',
            request
        ).then((res: AxiosResponse<RealOwnerAdditionalInformation>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getTransactionalProfile(
    kycId: number,
    expedientId: number
): Promise<EfeResponse<TransactionalProfileServiceRequest>> {
    try {
        return await EfeApi.Clients.get<TransactionalProfileServiceRequest>('/api/v1/clients/pld', {
            params: {kycId: kycId, expedientId: expedientId},
        }).then((res: AxiosResponse<TransactionalProfileServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getPersonalAddress(
    kycId: number,
    foreign: boolean,
    addressId: number
): Promise<EfeResponse<AddressServiceRequest>> {
    let url = '/api/v1/clients/personal/address';
    if (foreign) {
        url = '/api/v1/clients/foreigners/personal/address';
    }
    try {
        return await EfeApi.Clients.get<AddressServiceRequest>(`${url}/${kycId}/${addressId}`).then(
            (res: AxiosResponse<AddressServiceRequest>) => {
                return Promise.resolve({
                    data: res.data,
                    status: 'Success',
                    success: true,
                    code: res.status,
                    message: 'Ok',
                });
            }
        );
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getLegalRepresentativeData(
    kycId: number,
    expedientId: number
): Promise<EfeResponse<LegalRepresentativeServiceRequest>> {
    try {
        return await EfeApi.Clients.get<LegalRepresentativeServiceRequest>(
            `/api/v1/clients/personal/data/legal/representative/${kycId}/${expedientId}`
        ).then((res: AxiosResponse<LegalRepresentativeServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getLegalRepresentatives(
    kycFatherId: number
): Promise<EfeResponse<LegalRepresentativesResponse>> {
    try {
        return await EfeApi.Clients.get<LegalRepresentativesResponse>(
            '/api/v1/clients/personal/data/legal/representative',
            {
                params: {kycFatherId: kycFatherId},
            }
        ).then((res: AxiosResponse<LegalRepresentativesResponse>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getBeneficiaries(kycFatherId: number): Promise<EfeResponse<BeneficiariesResponse>> {
    try {
        return await EfeApi.Clients.get<BeneficiariesResponse>(
            '/api/v1/clients/beneficiaries/all',
            {
                params: {kycFatherId: kycFatherId},
            }
        ).then((res: AxiosResponse<BeneficiariesResponse>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getBeneficiary(kycId: number): Promise<EfeResponse<BeneficiaryServiceRequest>> {
    try {
        return await EfeApi.Clients.get<BeneficiaryServiceRequest>(
            `/api/v1/clients/beneficiaries/${kycId}`
        ).then((res: AxiosResponse<BeneficiaryServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveBeneficiary(
    data: BeneficiaryServiceRequest
): Promise<EfeResponse<BeneficiaryServiceRequest>> {
    try {
        return await EfeApi.Clients.put<BeneficiaryServiceRequest>(
            '/api/v1/clients/beneficiaries',
            data
        ).then((res: AxiosResponse<BeneficiaryServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getBeneficiaryAddress(kycId: number): Promise<EfeResponse<AddressServiceRequest>> {
    try {
        return await EfeApi.Clients.get<AddressServiceRequest>(
            `/api/v1/clients/beneficiaries/address/${kycId}`
        ).then((res: AxiosResponse<AddressServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveBeneficiaryAddress(
    data: AddressServiceRequest
): Promise<EfeResponse<AddressServiceRequest>> {
    try {
        return await EfeApi.Clients.put<AddressServiceRequest>(
            '/api/v1/clients/beneficiaries/address',
            data
        ).then((res: AxiosResponse<AddressServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getShareholders(kycFatherId: number): Promise<EfeResponse<ShareholdersResponse>> {
    try {
        return await EfeApi.Clients.get<ShareholdersResponse>(
            '/api/v1/clients/personal/data/shareholder',
            {
                params: {kycFatherId: kycFatherId, page: 1, pageSize: 100},
            }
        ).then((res: AxiosResponse<ShareholdersResponse>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getRealOwners(kycFatherId: number): Promise<EfeResponse<RealOwnersResponse>> {
    try {
        return await EfeApi.Clients.get<RealOwnersResponse>(
            '/api/v1/clients/personal/data/real/owner',
            {
                params: {kycFatherId: kycFatherId},
            }
        ).then((res: AxiosResponse<RealOwnersResponse>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getConstitutiveAct(
    kycId: number
): Promise<EfeResponse<ConstitutiveActServiceRequest>> {
    try {
        return await EfeApi.Clients.get<ConstitutiveActServiceRequest>(
            `/api/v1/clients/documents/constitutive/information/${kycId}`
        ).then((res: AxiosResponse<ConstitutiveActServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getLegalRepresentativeIdentification(
    kycId: number
): Promise<EfeResponse<InformationLegalRepresentativeServiceRequest>> {
    try {
        return await EfeApi.Clients.get<InformationLegalRepresentativeServiceRequest>(
            '/api/v1/clients/legals/representatives/identifications/data',
            {params: {kycId: kycId}}
        ).then((res: AxiosResponse<InformationLegalRepresentativeServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getClientPersonalData(
    kycId: number
): Promise<EfeResponse<PersonalDataServiceRequest>> {
    try {
        return await EfeApi.Clients.get<PersonalDataServiceRequest>(
            `/api/v1/clients/personal/data/${kycId}`,
            {params: {kycId: kycId}}
        ).then((res: AxiosResponse<PersonalDataServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getRealOwnerData(kycId: number): Promise<EfeResponse<RealOwnerServiceRequest>> {
    try {
        return await EfeApi.Clients.get<RealOwnerServiceRequest>(
            `/api/v1/clients/personal/data/real/owner/${kycId}`
        ).then((res: AxiosResponse<RealOwnerServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getShareholderInformation(
    kycId: number
): Promise<EfeResponse<ShareholderServiceRequest>> {
    try {
        return await EfeApi.Clients.get<ShareholderServiceRequest>(
            `/api/v1/clients/personal/data/shareholder/${kycId}`,
            {params: {kycId: kycId}}
        ).then((res: AxiosResponse<ShareholderServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getRealOwnerAddress(kycId: number): Promise<EfeResponse<AddressServiceRequest>> {
    try {
        return await EfeApi.Clients.get<AddressServiceRequest>(
            '/api/v1/clients/real/owner/address',
            {params: {kycId: kycId}}
        ).then((res: AxiosResponse<AddressServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getIdentificationData(
    kycId: number
): Promise<EfeResponse<IdentificationServiceRequest>> {
    try {
        return await EfeApi.Clients.get<IdentificationServiceRequest>(
            '/api/v1/clients/identifications/data',
            {params: {kycId: kycId}}
        ).then((res: AxiosResponse<IdentificationServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function validateInformation(
    kycId: number,
    expedientId: number
): Promise<EfeResponse<ValidateClientServiceRequest>> {
    try {
        return await EfeApi.Clients.get<ValidateClientServiceRequest>('/api/v1/clients/validate', {
            params: {kycId: kycId, expedientId: expedientId},
        }).then((res: AxiosResponse<ValidateClientServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function uploadSecondaryClients(request: DashboardUsersValues): Promise<EfeResponse<any>> {
    try {
        const formData = new FormData();
        formData.append('file', request.file, 'users.csv');
        formData.append('expedientId', request.expedientId.toString());
        formData.append('kycFatherId', request.kycFatherId.toString());
        formData.append('personKey', request.personKey);

        return await EfeApi.Clients.post<any>('/api/v1/clients/upload', formData, {
            headers: {
                'content-type': 'multipart/form-data',
            },
        }).then((res) => {
            return Promise.resolve({
                data: {},
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function signTermsConditions(
    expedientId: number
): Promise<EfeResponse<SignTermsConditionsResponse>> {
    try {
        return await EfeApi.Clients.put(
            `/api/v1/clients/enrollment/terms/conditions?expedientId=${expedientId}`
        ).then((res: AxiosResponse<SignTermsConditionsResponse>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getExpedientStatus(expedientId: number): Promise<EfeResponse<ExpedientResponse>> {
    try {
        return await EfeApi.Clients.get(`/api/v1/clients/status?expedientId=${expedientId}`).then(
            (res: AxiosResponse<ExpedientResponse>) => {
                return Promise.resolve({
                    data: res.data,
                    status: 'Success',
                    success: true,
                    code: res.status,
                    message: 'Ok',
                });
            }
        );
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveGeolocationData(
    expedientId: number,
    data: GeolocationInfo
): Promise<EfeResponse<SyncClientDataResponse>> {
    try {
        return await EfeApi.Clients.put<SyncClientDataResponse>(
            '/api/v1/clients/status/geolocation',
            {
                expedientId: expedientId,
                latitude: data.latitude,
                longitude: data.longitude,
            }
        ).then((res: AxiosResponse<SyncClientDataResponse>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function deleteBeneficiary(kycId: number) {
    try {
        return await EfeApi.Clients.delete<SyncClientDataResponse>(
            `/api/v1/clients/beneficiaries/${kycId}`
        ).then((res: AxiosResponse<SyncClientDataResponse>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function updateAuthorization(
    expedientId: number,
    isAuthorized: boolean
): Promise<EfeResponse<SyncClientDataResponse>> {
    try {
        return await EfeApi.Clients.put<SyncClientDataResponse>(
            `/api/v1/clients/beneficiaries/${expedientId}/authorization?isAuthorized=${isAuthorized}`
        ).then((res: AxiosResponse<SyncClientDataResponse>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getBeneficiariesAuth(
    expedientId: number
): Promise<EfeResponse<BeneficiariesAuthorization>> {
    try {
        return await EfeApi.Clients.get<BeneficiariesAuthorization>(
            `/api/v1/clients/beneficiaries/${expedientId}/authorization`
        ).then((res: AxiosResponse<BeneficiariesAuthorization>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveUserConsigneesConfig(
    values: ConsigneeTerms
): Promise<EfeResponse<SyncClientDataResponse>> {
    try {
        return await EfeApi.Clients.put<SyncClientDataResponse>(
            '/api/v1/clients/enrollment/consignees/accounts',
            values
        ).then((res: AxiosResponse<SyncClientDataResponse>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function userHasConsignees(expedientId: number): Promise<EfeResponse<ConsigneeTerms>> {
    try {
        return await EfeApi.Clients.get<ConsigneeTerms>(
            `/api/v1/clients/enrollment/consignees/accounts?expedientId=${expedientId}`
        ).then((res: AxiosResponse<ConsigneeTerms>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getConsignees(
    kycFatherId: number,
    page: number,
    pageSize: number
): Promise<EfeResponse<ConsigneesResponse>> {
    try {
        return await EfeApi.Clients.get<ConsigneesResponse>(
            `/api/v1/clients/consignees/all?kycFatherId=${kycFatherId}&page=${page}&pageSize=${pageSize}`
        ).then((res: AxiosResponse<ConsigneesResponse>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveConsigneeData(values: ConsigneeData): Promise<EfeResponse<ConsigneeData>> {
    try {
        return await EfeApi.Clients.put<ConsigneeData>('/api/v1/clients/consignees', values).then(
            (res: AxiosResponse<ConsigneeData>) => {
                return Promise.resolve({
                    data: res.data,
                    status: 'Success',
                    success: true,
                    code: res.status,
                    message: 'Ok',
                });
            }
        );
    } catch (error) {
        return Promise.reject(error);
    }
}

async function saveConsigneeAddress(
    values: AddressServiceRequest
): Promise<EfeResponse<AddressServiceRequest>> {
    try {
        return await EfeApi.Clients.put<AddressServiceRequest>(
            '/api/v1/clients/consignees/address',
            values
        ).then((res: AxiosResponse<AddressServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getConsigneeData(kycId: number): Promise<EfeResponse<ConsigneeData>> {
    try {
        return await EfeApi.Clients.get<ConsigneeData>(`/api/v1/clients/consignees/${kycId}`).then(
            (res: AxiosResponse<ConsigneeData>) => {
                return Promise.resolve({
                    data: res.data,
                    status: 'Success',
                    success: true,
                    code: res.status,
                    message: 'Ok',
                });
            }
        );
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getConsigneeAddress(kycId: number): Promise<EfeResponse<AddressServiceRequest>> {
    try {
        return await EfeApi.Clients.get<AddressServiceRequest>(
            `/api/v1/clients/consignees/address/${kycId}`
        ).then((res: AxiosResponse<AddressServiceRequest>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getLoadUsers(kycFatherId: number): Promise<EfeResponse<ConsigneeCatalogResponse>> {
    try {
        return await EfeApi.Clients.get<ConsigneeCatalogResponse>(
            `/api/v1/clients/consignees?kycFatherId=${kycFatherId}`
        ).then((res: AxiosResponse<ConsigneeCatalogResponse>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function consigneeContractSigned(
    personKey: string
): Promise<EfeResponse<ConsigneeConfirmation>> {
    try {
        return await EfeApi.Clients.put<ConsigneeConfirmation>(
            `/api/v1/clients/consignees/confirmation?personKey=${personKey}`
        ).then((res: AxiosResponse<ConsigneeConfirmation>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getUsers(
    page: number,
    perPage: number,
    filterValue: string
): Promise<EfeResponse<ClientResponse>> {
    try {
        let filter = '';
        if (filterValue) {
            filter = `&filterValue=${filterValue}`;
        }
        return await EfeApi.Clients.get<ClientResponse>(
            `/api/v1/clients?page=${page}&pageSize=${perPage}${filter}`
        ).then((res: AxiosResponse<ClientResponse>) => {
            return Promise.resolve({
                data: res.data,
                status: 'Success',
                success: true,
                code: res.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getMailAuthentication(kycId: number): Promise<EfeResponse<MailAuthResponse>> {
    try {
        return await EfeApi.Clients.get<MailAuthResponse>(
            `/api/v1/clients/authentication/mail/${kycId}`
        ).then((response: AxiosResponse<MailAuthResponse>) => {
            return Promise.resolve({
                data: response.data,
                status: 'Success',
                success: true,
                code: response.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function validateMailAuthentication(
    kycId: number,
    code: string
): Promise<EfeResponse<OtpValidation>> {
    try {
        return await EfeApi.Clients.post<OtpValidation>('/api/v1/clients/authentication/mail', {
            kycId: kycId,
            token: code,
        }).then((response: AxiosResponse<OtpValidation>) => {
            return Promise.resolve({
                data: response.data,
                status: 'Success',
                success: true,
                code: response.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getAccountData(rfc: string): Promise<EfeResponse<MailAuthResponse>> {
    try {
        return await EfeApi.Clients.get<MailAuthResponse>(`/api/v1/clients/${rfc}/account`).then(
            (response: AxiosResponse<MailAuthResponse>) => {
                return Promise.resolve({
                    data: response.data,
                    status: 'Success',
                    success: true,
                    code: response.status,
                    message: 'Ok',
                });
            }
        );
    } catch (error) {
        return Promise.reject(error);
    }
}

async function notifyConsignee(kycId: number): Promise<EfeResponse<any>> {
    try {
        return await EfeApi.Clients.post<any>(`/api/v1/clients/consignees/registration/notify?personKey=${kycId}`).then(
            (response: AxiosResponse<any>) => {
                return Promise.resolve({
                    data: response.data,
                    status: 'Success',
                    success: true,
                    code: response.status,
                    message: 'Ok',
                });
            }
        );
    } catch (error) {
        return Promise.reject(error);
    }
}

async function syncAccountConsignee(values: ConsigneeTerms): Promise<EfeResponse<any>> {
    try {
        return await EfeApi.Clients.put<any>(
            '/api/v1/clients/enrollment/consignees/accounts?amlSync=true',
            values
        ).then((response: AxiosResponse<any>) => {
            return Promise.resolve({
                data: response.data,
                status: 'Success',
                success: true,
                code: response.status,
                message: 'Ok',
            });
        });
    } catch (error) {
        return Promise.reject(error);
    }
}

async function getLogs(request: LogErrorsRequest): Promise<EfeResponse<LogErrorsResponse>>{
    try {
        return await EfeApi.Logs.get<any>(
            '/api/v1/logs', {params: request}
        ).then((response: AxiosResponse<any>) => {
            return Promise.resolve({
                data: response.data,
                status: 'Success',
                success: true,
                code: response.status,
                message: 'Ok',
            });
        });
    } catch (e) {
        return Promise.reject(e);
    }
}

async function getLogDetail(request: LogDetailRequest): Promise<EfeResponse<LogDetailResponse>>{
    try {
        return await EfeApi.Logs.get<any>(
            '/api/v1/logs/errors/details', {params: request}
        ).then((response: AxiosResponse<any>) => {
            return Promise.resolve({
                data: response.data,
                status: 'Success',
                success: true,
                code: response.status,
                message: 'Ok',
            });
        });
    } catch (e) {
        return Promise.reject(e);
    }
}

export default {
    getWorkflow,
    getRealOwnerAdditionalInformation,
    getTransactionalProfile,
    getPersonalAddress,
    getLegalRepresentativeData,
    getConstitutiveAct,
    getLegalRepresentativeIdentification,
    getClientPersonalData,
    getShareholderInformation,
    getRealOwnerAddress,
    getIdentificationData,
    getClientContact,
    getLegalRepresentatives,
    getBeneficiaries,
    getBeneficiary,
    saveBeneficiary,
    deleteBeneficiary,
    updateAuthorization,
    getBeneficiariesAuth,
    getBeneficiaryAddress,
    saveBeneficiaryAddress,
    getShareholders,
    getRealOwners,
    getRealOwnerData,
    validateInformation,
    updatePersonalData,
    updateConstitutiveAct,
    saveTransactionProfile,
    saveClientContact,
    saveIdentificationData,
    saveLegalRepresentative,
    saveLegalRepresentativeOfficialInformationData,
    saveRealOwner,
    saveWorkflow,
    saveAddress,
    saveRealOwnerAddress,
    saveShareholderData,
    saveRealOwnerAdditionalInformation,
    syncClientData,
    uploadSecondaryClients,
    signTermsConditions,
    getExpedientStatus,
    saveGeolocationData,
    userHasConsignees,
    saveUserConsigneesConfig,
    getConsignees,
    saveConsigneeData,
    saveConsigneeAddress,
    getConsigneeData,
    getConsigneeAddress,
    getLoadUsers,
    consigneeContractSigned,
    getUsers,
    getMailAuthentication,
    validateMailAuthentication,
    getAccountData,
    notifyConsignee,
    syncAccountConsignee,
    getLogs,
    getLogDetail,
};
